import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import {
    ClipboardDocumentListIcon,
    UserGroupIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    PaperAirplaneIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface TaskApplication {
    _id: string;
    status: 'invited' | 'applied' | 'approved' | 'rejected';
    appliedAt: string | null;
    reviewedAt: string | null;
    assignedDesignation?: string;
    applicationMessage?: string;
    rejectionReason?: string;
    createdAt: string;
    user: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        phone?: string;
        region?: string;
        qualification?: string;
        experience?: string;
        designation?: string;
        skills?: Array<{
            _id: string;
            name: string;
        }>;
        personalDetails?: {
            address?: {
                state?: string;
                district?: string;
            };
        };
    };
    task: {
        _id: string;
        title: string;
        description: string;
        company: {
            companyName: string;
        };
    };
    reviewedBy?: {
        firstName: string;
        lastName: string;
        email: string;
    };
}

const AdminTaskApplications: React.FC = () => {
    const dispatch = useAppDispatch();
    const [applications, setApplications] = useState<TaskApplication[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState<TaskApplication | null>(null);
    const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
    const [selectedDesignation, setSelectedDesignation] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');

    const fetchApplications = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/task-applications', {
                params: {
                    status: activeTab === 'all' ? undefined : activeTab,
                    page: currentPage,
                    limit: 10
                }
            });

            const { applications: apps, pagination } = response.data.data;
            setApplications(apps);
            setTotalPages(pagination.totalPages);
            setTotalItems(pagination.totalItems);
        } catch (error: any) {
            console.error('Error fetching applications:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch applications',
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    }, [activeTab, currentPage, dispatch]);

    useEffect(() => {
        fetchApplications();
    }, [fetchApplications]);

    const handleReviewApplication = async () => {
        if (!selectedApplication) return;

        try {
            const response = await api.put(`/admin/task-applications/${selectedApplication._id}/review`, {
                action: reviewAction,
                designation: reviewAction === 'approve' ? selectedDesignation : undefined,
                rejectionReason: reviewAction === 'reject' ? rejectionReason : undefined
            });

            dispatch(showToast({
                message: response.data.message,
                type: 'success'
            }));

            setShowReviewModal(false);
            setSelectedApplication(null);
            setReviewAction('approve');
            setSelectedDesignation('');
            setRejectionReason('');
            fetchApplications();
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || `Failed to ${reviewAction} application`,
                type: 'error'
            }));
        }
    };

    const openReviewModal = (application: TaskApplication, action: 'approve' | 'reject') => {
        setSelectedApplication(application);
        setReviewAction(action);
        setSelectedDesignation('');
        setRejectionReason('');
        setShowReviewModal(true);
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'invited':
                return <ClockIcon className="h-5 w-5 text-yellow-500" />;
            case 'applied':
                return <PaperAirplaneIcon className="h-5 w-5 text-blue-500" />;
            case 'approved':
                return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
            case 'rejected':
                return <XCircleIcon className="h-5 w-5 text-red-500" />;
            default:
                return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'invited':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'applied':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'approved':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'rejected':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'invited':
                return 'Invited';
            case 'applied':
                return 'Applied';
            case 'approved':
                return 'Approved';
            case 'rejected':
                return 'Rejected';
            default:
                return 'Unknown';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 container mx-auto px-4 py-8">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            Task Applications Management
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">
                            Review and manage all task applications across the platform
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-right">
                            <p className="text-sm text-gray-500 dark:text-gray-400">Total Applications</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalItems}</p>
                        </div>
                        <UserGroupIcon className="h-8 w-8 text-blue-600" />
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="flex space-x-8 px-6">
                        {[
                            { key: 'all', label: 'All Applications', count: totalItems },
                            // { key: 'invited', label: 'Invitations', count: applications.filter(app => app.status === 'invited').length },
                            { key: 'applied', label: 'Pending Review', count: applications.filter(app => app.status === 'applied').length },
                            { key: 'approved', label: 'Approved', count: applications.filter(app => app.status === 'approved').length },
                            { key: 'rejected', label: 'Rejected', count: applications.filter(app => app.status === 'rejected').length }
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => {
                                    setActiveTab(tab.key);
                                    setCurrentPage(1);
                                }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${activeTab === tab.key
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                    }`}
                            >
                                {tab.label}
                                <span className={`px-2 py-1 rounded-full text-xs ${activeTab === tab.key
                                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-200'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                                    }`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Applications List */}
                <div className="p-6">
                    {applications.length === 0 ? (
                        <div className="text-center py-12">
                            <ClipboardDocumentListIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                No applications found
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400">
                                {activeTab === 'all'
                                    ? "No applications have been submitted yet."
                                    : `No ${activeTab} applications found.`
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {applications.map((application) => (
                                <div
                                    key={application._id}
                                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-3">
                                                <UserGroupIcon className="h-5 w-5 text-gray-400" />
                                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    {application.user.firstName} {application.user.lastName}
                                                </h3>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                                                    {getStatusText(application.status)}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                                                <div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Email:</strong> {application.user.email}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Phone:</strong> {application.user.phone || 'Not provided'}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Region:</strong> {application.user.region || 'Not specified'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Skills:</strong> {application.user.skills?.map((skill: any) => skill.name).join(', ') || 'Not specified'}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Qualification:</strong> {application.user.qualification || 'Not specified'}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Experience:</strong> {application.user.experience || 'Not specified'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Task:</strong> {application.task.title}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Company:</strong> {application.task.company.companyName}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Applied:</strong> {application.appliedAt ? formatDate(application.appliedAt) : 'Not applied yet'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Location:</strong> {application.user.personalDetails?.address?.state || 'Not specified'}, {application.user.personalDetails?.address?.district || 'Not specified'}
                                                    </p>

                                                    {application.assignedDesignation && (
                                                        <p className="text-sm text-green-600 dark:text-green-400">
                                                            <strong>Assigned for Task:</strong> {application.assignedDesignation.replace('_', ' ')}
                                                        </p>
                                                    )}
                                                    {!application.assignedDesignation && application.status === 'applied' && (
                                                        <p className="text-sm text-orange-600 dark:text-orange-400">
                                                            <strong>Task Designation:</strong> Pending assignment
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            {application.applicationMessage && (
                                                <div className="mb-4">
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Application Message:</strong>
                                                    </p>
                                                    <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">
                                                        {application.applicationMessage}
                                                    </p>
                                                </div>
                                            )}

                                            {application.assignedDesignation && (
                                                <div className="mb-4">
                                                    <p className="text-sm text-green-600 dark:text-green-400">
                                                        <strong>Assigned Designation:</strong> {application.assignedDesignation.replace('_', ' ')}
                                                    </p>
                                                </div>
                                            )}

                                            {application.rejectionReason && (
                                                <div className="mb-4">
                                                    <p className="text-sm text-red-600 dark:text-red-400">
                                                        <strong>Rejection Reason:</strong>
                                                    </p>
                                                    <p className="text-sm text-red-800 dark:text-red-200 mt-1">
                                                        {application.rejectionReason}
                                                    </p>
                                                </div>
                                            )}

                                            {application.reviewedBy && (
                                                <div className="mb-4">
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Reviewed by:</strong> {application.reviewedBy.firstName} {application.reviewedBy.lastName}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Reviewed at:</strong> {application.reviewedAt ? formatDate(application.reviewedAt) : 'Not reviewed'}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-2 ml-4">
                                            {getStatusIcon(application.status)}

                                            {application.status === 'applied' && (
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => openReviewModal(application, 'approve')}
                                                        className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium"
                                                    >
                                                        <CheckCircleIcon className="h-4 w-4" />
                                                        Approve
                                                    </button>
                                                    <button
                                                        onClick={() => openReviewModal(application, 'reject')}
                                                        className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 text-sm font-medium"
                                                    >
                                                        <XCircleIcon className="h-4 w-4" />
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-700 dark:text-gray-300">
                                Showing page {currentPage} of {totalPages} ({totalItems} total applications)
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Review Modal */}
            {showReviewModal && selectedApplication && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                {reviewAction === 'approve' ? 'Approve Application' : 'Reject Application'}
                            </h3>

                            {/* User Details Section */}
                            <div className="mb-6 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                                    <UserGroupIcon className="h-5 w-5 mr-2" />
                                    User Details
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            <strong>Name:</strong> {selectedApplication.user.firstName} {selectedApplication.user.lastName}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            <strong>Email:</strong> {selectedApplication.user.email}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            <strong>Phone:</strong> {selectedApplication.user.phone || 'Not provided'}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            <strong>Region:</strong> {selectedApplication.user.region || 'Not specified'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            <strong>Skills:</strong> {selectedApplication.user.skills?.map((skill: any) => skill.name).join(', ') || 'Not specified'}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            <strong>Qualification:</strong> {selectedApplication.user.qualification || 'Not specified'}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            <strong>Experience:</strong> {selectedApplication.user.experience || 'Not specified'}
                                        </p>

                                        {selectedApplication.assignedDesignation && (
                                            <p className="text-sm text-green-600 dark:text-green-400">
                                                <strong>Assigned for Task:</strong> {selectedApplication.assignedDesignation.replace('_', ' ')}
                                            </p>
                                        )}
                                        {!selectedApplication.assignedDesignation && selectedApplication.status === 'applied' && (
                                            <p className="text-sm text-orange-600 dark:text-orange-400">
                                                <strong>Task Designation:</strong> Pending assignment
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        <strong>Location:</strong> {selectedApplication.user.personalDetails?.address?.state || 'Not specified'}, {selectedApplication.user.personalDetails?.address?.district || 'Not specified'}
                                    </p>
                                </div>
                            </div>

                            {/* Task Details Section */}
                            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                    Task Details
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    <strong>Task:</strong> {selectedApplication.task.title}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    <strong>Company:</strong> {selectedApplication.task.company.companyName}
                                </p>
                            </div>

                            {reviewAction === 'approve' && (
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Assign Task-Specific Designation *
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                        This designation will be applied only for this specific task. The user can have different designations for different tasks.
                                    </p>
                                    <select
                                        value={selectedDesignation}
                                        onChange={(e) => setSelectedDesignation(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">Select designation for this task</option>
                                        <option value="team_executive">Team Executive</option>
                                        <option value="team_leader">Team Leader</option>
                                        <option value="district_head">District Head</option>
                                        <option value="state_head">State Head</option>
                                        <option value="regional_head">Regional Head</option>
                                    </select>
                                </div>
                            )}

                            {reviewAction === 'reject' && (
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Rejection Reason (Optional)
                                    </label>
                                    <textarea
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        placeholder="Provide a reason for rejection..."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        rows={3}
                                    />
                                </div>
                            )}

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setShowReviewModal(false);
                                        setSelectedApplication(null);
                                        setReviewAction('approve');
                                        setSelectedDesignation('');
                                        setRejectionReason('');
                                    }}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleReviewApplication}
                                    disabled={reviewAction === 'approve' && !selectedDesignation}
                                    className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${reviewAction === 'approve'
                                        ? 'bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed'
                                        : 'bg-red-600 text-white hover:bg-red-700'
                                        }`}
                                >
                                    {reviewAction === 'approve' ? (
                                        <>
                                            <CheckCircleIcon className="h-4 w-4" />
                                            Approve & Assign Designation
                                        </>
                                    ) : (
                                        <>
                                            <XCircleIcon className="h-4 w-4" />
                                            Reject Application
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTaskApplications;
